import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Typography, Space, Spin, Table, Image } from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  FolderOpenOutlined,
  DatabaseOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import GopYForm from "../GopYForm";
import { apiCaller } from "src/util/apiCaller";
import { ColumnType } from "antd/es/table";

const { Title, Paragraph, Text } = Typography;

interface HistoricalDocumentDetail {
  idTulieu: number;
  tenTuLieu: string;
  moTa: string;
  chiTiet: string;
  anhDaiDien: string;
  ngayDang: string;
  ngayDuyetXuatBan: string;
  historicalPeriod: string;
  tacGia: string;
  videos?: string[];
  listTuLieuFile: {
    id: number;
    idTuLieu: number;
    tenFile: string;
    duongDanFile: string;
    moTa: string;
    tacGia: string;
    idNguoiDang: number;
    ngayDang: number;
    isSuDung: boolean;
  }[];
}

const DetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState<HistoricalDocumentDetail | null>(
    null
  );

  useEffect(() => {
    if (id) {
      fetchDocumentDetail(id);
    }
  }, [id]);

  const fetchDocumentDetail = async (id: string) => {
    setLoading(true);
    try {
      const response = await apiCaller({
        url: `/apipublic/tulieuhienthi/${id}`,
        method: "GET",
      });

      if (response?.data) {
        const doc = response.data;
        const formattedDocument: HistoricalDocumentDetail = {
          idTulieu: doc.idTulieu || 0,
          tenTuLieu: doc.tenTuLieu || "",
          moTa: doc.moTa || "",
          chiTiet: doc.chiTiet || "",
          anhDaiDien: doc.anhDaiDien || "",
          ngayDang: doc.ngayDang || "",
          ngayDuyetXuatBan: doc.ngayDuyetXuatBan || "",
          historicalPeriod: doc.loaiTuLieu?.tenLoaiTuLieu || "",
          tacGia: doc.tacGia || "",
          videos: doc.videos || [],
          listTuLieuFile: doc.listTuLieuFile || [],
        };
        setDocument(formattedDocument);
      }
    } catch (error) {
      console.error("Error fetching document detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnType<{
    id: number;
    tenFile: string;
    duongDanFile: string;
    tacGia: string;
    moTa: string;
    key: number;
  }>[] = [
    {
      title: "STT",
      dataIndex: "key",
      key: "key",
      width: "5%",
      align: "center",
    },
    {
      title: "Tác giả",
      dataIndex: "tacGia",
      key: "tacGia",
      render: (text: string) => <div>{text}</div>,
    },
    {
      title: "Mô tả",
      key: "duongDanFile",
      render: (
        text: string,
        record: { duongDanFile: string; tenFile: string; moTa: string }
      ) => (
        <>
          <div>{record.moTa}</div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <PaperClipOutlined style={{ marginRight: 4 }} />
            <a
              href={record.duongDanFile}
              rel="noopener noreferrer"
            >
              <Text
                style={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  flex: "1 1 auto",
                  color: "#0e5ace"
                }}
                strong
              >
                {record.tenFile}
              </Text>
            </a>
          </div>
        </>
      ),
    },
  ];

  const data =
    document?.listTuLieuFile.map((file, index) => ({
      key: index + 1,
      ...file,
    })) || [];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!document) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Title level={3}>Không tìm thấy tài liệu</Title>
      </div>
    );
  }

  return (
    <div className="QTI-content-detail">
      <Title level={4}>
        <DatabaseOutlined /> {document.tenTuLieu}
      </Title>
      <Space
        direction="vertical"
        size={16}
        style={{ marginBottom: 24 }}
        className="QTI-detail-page-author"
      >
        <Space direction="horizontal" size={0}>
          <UserOutlined />
          <Text strong>{document.tacGia}</Text>
          <Text type="secondary">
            <CalendarOutlined />{" "}
            {new Date(document.ngayDang).toLocaleDateString("vi-VN")}
          </Text>
          <Text type="secondary">
            <FolderOpenOutlined /> {document.historicalPeriod}
          </Text>
        </Space>
      </Space>
      <Paragraph strong style={{ fontSize: 16 }}>
        {document.moTa}
      </Paragraph>
      <div className="QTI-img-center">
        <Image
          src={document.anhDaiDien}
          alt={document.tenTuLieu}
        />
      </div>
      <div
        dangerouslySetInnerHTML={{ __html: document.chiTiet }}
        style={{ marginBottom: 24 }}
      />

      <Title level={3}>
        <DatabaseOutlined /> Danh sách tư liệu
      </Title>
      <div style={{ marginTop: "10px", marginBottom: "20px" }}>
        <Table
          bordered
          rowClassName={(record, index) =>
            index % 2 === 0 ? "qti-table-row-background" : ""
          }
          size="middle"
          className="qti-table-wrapper gx-table-responsive"
          columns={columns}
          dataSource={data}
          pagination={false}
          rowKey="key"
        />
      </div>
      <GopYForm />
    </div>
  );
};

export default DetailPage;
